import attendanceService from 'ApiService/Teacher/Attendance';
import learningMaterial from 'ApiService/Teacher/learningMaterial';
import Card from 'components/card';
import Nodatafound from 'pages/Common/Nodatafound';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import toast from 'react-hot-toast';
import { MdSearch } from 'react-icons/md';
import { BallTriangle } from 'react-loader-spinner';
import TopicSelectionModal from '../Components/TopicSelectionModal';

const AttendancesSheet = ({
  classData,
  attendance,
  handleCheckboxChange,
  handleMarkAllAttendance,
  getAttendanceMarkAllState,
  getStudents,
  date,
  setDate,
  lectureData,
  setLectureData,
  selectedSubtopics,
  setSelectedSubtopics,
  attendanceSubmitted,
  completedStudents,
  completedCourses,
  batchCourses,
  setShowCourseCompleteModal,
}) => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [sentTopics, setSentTopics] = useState([]);

  // Check if all courses in the batch are completed
  const allCoursesCompleted = (batchCourses || []).length > 0 && (batchCourses || []).every(
    (bc) => (completedCourses || []).some((cc) => cc.course?.toString() === bc._id?.toString())
  );

  useEffect(() => {
    const filtered = attendance.filter((student) =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAttendance(filtered);
  }, [attendance, searchTerm]);

  const fetchTrainingMaterial = async () => {
    try {
      const res = await learningMaterial.getTrainingMaterial(
        classData.batchId,
        classData.courseId
      );

      if (res.data.success && res.data.data) {
        setLectureData(res.data.data.days || []);
      } else {
        setLectureData([]);
      }
    } catch (err) {
      console.error('Training material error:', err);
      setLectureData([]);
    }
  };

  const fetchSentTopics = async () => {
    if (!classData.courseId || !classData.batchId) return;
    try {
      const res = await attendanceService.getSentTopics(
        classData.courseId,
        classData.batchId
      );
      if (res.data.success) {
        setSentTopics(res.data.sentTopics || []);
      }
    } catch (err) {
      console.error('Error fetching sent topics:', err);
    }
  };

  useEffect(() => {
    if (classData && classData.courseId && classData.batchId) {
      setLoading(true);
      getStudents().finally(() => setLoading(false));
      fetchTrainingMaterial();
      fetchSentTopics();
    }
  }, [classData?.courseId, classData?.batchId, date?.getTime?.()]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <Card extra={'w-full sm:overflow-auto p-4 '}>
      <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col">
          <div className="text-sm font-medium">Select Date:</div>
          <DatePicker
            selected={date}
            onChange={(selectedDate) => setDate(selectedDate)}
            className="flex justify-end rounded border px-2 py-1 dark:bg-navy-800"
            maxDate={new Date()}
          />
        </div>

        <div className="flex-grow md:max-w-xs">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
            />
            <MdSearch className="absolute left-3 top-2.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
          {batchCourses && batchCourses.length > 0 && (
            <div className="mt-4 md:mt-0">
              <button
                onClick={() => setShowCourseCompleteModal(true)}
                className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
              >
                Complete Course
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
        <span className="flex items-center gap-1">
          <span className="inline-block h-4 w-4 rounded bg-green-100 text-center font-bold text-green-700 dark:bg-green-900/30 dark:text-green-400">
            P
          </span>
          Present
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-4 w-4 rounded bg-red-100 text-center font-bold text-red-700 dark:bg-red-900/30 dark:text-red-400">
            A
          </span>
          Absent
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-4 w-4 rounded border border-gray-300 text-center font-bold text-gray-400">
            -
          </span>
          Not marked (doesn't count in attendance %)
        </span>
      </div>

      <div>
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="flex h-40 items-center justify-center">
              <BallTriangle color="#5431ed" />
            </div>
          </div>
        ) : (
          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            {lectureData && lectureData.length > 0 && (
              allCoursesCompleted ? (
                <div className="mb-4 rounded-lg bg-yellow-100 px-4 py-2 text-sm font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                  ⚠️ Batch is fully completed — no new study material can be sent.
                </div>
              ) : (
                <button
                  onClick={() => setShowTopicModal(true)}
                  className={`mb-4 rounded-lg px-4 py-2 font-medium text-white transition ${
                    selectedSubtopics.length > 0
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-brand-900 hover:bg-brand-800'
                  }`}
                >
                  {selectedSubtopics.length > 0
                    ? `Select Topics (${selectedSubtopics.length})`
                    : 'Select Topics'}
                </button>
              )
            )}

            {filteredAttendance.length > 0 ? (
              <table className="w-full">
                <thead className="w-full">
                  <tr className="items-center border-b border-gray-200 dark:border-white/10">
                    <th className="px-[27px] pb-[12px] pt-[32px] text-center text-xs tracking-wide text-gray-600">
                      <div className="text-xs font-bold">SR.NO</div>
                    </th>
                    <th className="pb-[12px] pt-[32px] text-center text-xs tracking-wide text-gray-600">
                      <div className="text-xs font-bold">ATTENDANCE MARK</div>
                      {handleMarkAllAttendance && (
                        <div className="mt-1 flex items-center justify-center">
                          {(() => {
                            const state = getAttendanceMarkAllState
                              ? getAttendanceMarkAllState()
                              : 'mixed';
                            const label =
                              state === 'mixed' || state === 'null'
                                ? '-'
                                : state === 'true'
                                  ? 'P'
                                  : 'A';
                            return (
                              <button
                                onClick={handleMarkAllAttendance}
                                title="Mark all students (-/P/A)"
                                className={`flex h-6 w-6 items-center justify-center rounded border text-[10px] font-bold transition ${
                                  state === 'true'
                                    ? 'border-green-500 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : state === 'false'
                                      ? 'border-red-500 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                      : 'border-gray-300 text-gray-400 hover:bg-gray-100 dark:border-navy-500 dark:hover:bg-navy-700'
                                }`}
                              >
                                {label}
                              </button>
                            );
                          })()}
                        </div>
                      )}
                    </th>
                    <th className="px-[27px] pb-[12px] pt-[32px] text-center text-xs tracking-wide text-gray-600">
                      <div className="text-xs font-bold">NAME</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendance.map((student, index) => {
                    const isCompleted = student.isCompleted;
                    return (
                      <tr
                        key={student.id}
                        className={`items-center border-b dark:border-white/10 ${
                          isCompleted
                            ? 'bg-gray-50 dark:bg-gray-800/50'
                            : 'border-gray-200'
                        }`}
                      >
                        <td className="mt-[20px] px-[27px] py-[16px]">
                          <div
                            className={`w-full text-center font-medium ${
                              isCompleted
                                ? 'text-gray-400 line-through'
                                : 'text-navy-700 dark:text-white'
                            }`}
                          >
                            {index + 1}
                          </div>
                        </td>
                        <td className="mt-[20px] px-[27px] py-[16px]">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() =>
                                !isCompleted && handleCheckboxChange(student.id)
                              }
                              disabled={isCompleted}
                              className={`h-7 w-7 rounded text-sm font-bold transition ${
                                student.isPresent === null
                                  ? 'text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700'
                                  : student.isPresent
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                              }`}
                            >
                              {student.isPresent === null
                                ? '-'
                                : student.isPresent
                                  ? 'P'
                                  : 'A'}
                            </button>
                          </div>
                        </td>
                        <td className="mt-[20px] px-[27px] py-[16px]">
                          <div
                            className={`w-full text-center font-medium ${
                              isCompleted
                                ? 'text-gray-400 line-through'
                                : 'text-navy-700 dark:text-white'
                            }`}
                          >
                            {student.name}
                            {student.addedOn && (
                              <div className="text-[10px] font-normal text-gray-500">
                                Added:{' '}
                                {new Date(student.addedOn).toLocaleDateString('en-GB', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </div>
                            )}
                            {isCompleted && (
                              <span className="ml-2 text-xs text-gray-500">
                                (Completed Course)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="mt-[20px] px-[27px] py-[16px]"></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="flex items-center justify-center dark:text-gray-600">
                <p className="mt-5 text-center ">
                  {searchTerm &&
                  (attendance.length > 0 || completedStudents.length > 0) ? (
                    <Nodatafound
                      title={`No students found matching "${searchTerm}".`}
                    />
                  ) : (
                    <Nodatafound title="No attendance data found." />
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <TopicSelectionModal
        isOpen={showTopicModal}
        onClose={() => setShowTopicModal(false)}
        lectureData={lectureData}
        selectedSubtopics={selectedSubtopics}
        setSelectedSubtopics={setSelectedSubtopics}
        sentTopics={sentTopics}
      />
    </Card>
  );
};

export default AttendancesSheet;
